<template>
  <div id="app" class="body-wrapper container-fluid ps-0 pe-0" :class="showWizard ? 'wizard-padding' : ''">
    <div v-if="appInitComplete && dataReady">
      <component :is="layout" ref="layoutRef">
        <router-view :key="key" />
      </component>
    </div>
    <div v-else id="loader">
      <loading
        :active="true"
        :can-cancel="false"
        :is-full-page="true"
        :color="'$primary'"
        :background-color="'#ababab'"
        :loader="'dots'"
      />
    </div>

    <ax-shared-popup
      :size="'md'"
      :id="'modalSync'"
      ref="modalSync"
    >
      <template #body>
        <sync-dialog v-if="appInitComplete" ref="syncPopup" />
      </template>
    </ax-shared-popup>

    <ax-shared-popup
        :size="'xl'"
        :id="'importReview'"
        ref="importReview"
      >
      <template #body>
        <review-import v-if="appInitComplete && importData"
          :data="importData"
          typeImport="xero"
          @onClickNextReviewImport="submitReview()"
          @onClickBackReviewImport="cancelImport()"
        ></review-import>
      </template>
    </ax-shared-popup>

    <ax-shared-popup
      :size="'md'"
      :id="'multipleConnectionsModal'"
      ref="multipleConnectionsModal"
    >
      <template #body>
        <multiple-connections
          v-if="appInitComplete && connectionData"
          ref="multipleConnections"
          :connectionData="connectionData"
          @onClickSubmitChosenConnection="submitConnection"
        ></multiple-connections>
      </template>
    </ax-shared-popup>

    <!-- Organisation Select -->
    <ax-shared-popup
      :size="'xl'"
      :id="'modalOrganisationSelect'"
      ref="modalOrganisationSelect"
    >
      <template #body>
        <organisation-select v-if="appInitComplete && isAuthenticated && !isAdmin"
          :allowClose="allowOrgSelectClose"
          @onClose="hideOrgSelectModal"
          @onAddNewOrganisation="tryShowWizard"
        />
      </template>
    </ax-shared-popup>

    <!-- Subscription -->
    <ax-shared-popup
      v-if="isAuthenticated"
      :size="subSize"
      :id="'modalSubscription'"
      ref="modalSubscription"
    >
      <template #body>
        <subscription-dialog v-if="appInitComplete && (businessIDContext || isAffiliate)"
          :businessID="businessIDContext"
          @onClose="hideSubscriptionModal"
          ref="subscriptionDialog"
          @modalSizeChange="subSize = $event"
        />
      </template>
    </ax-shared-popup>

    <!-- New Org Wizard -->
    <ax-shared-popup
      :size="wizardModalSize"
      :id="'modalWizard'"
      ref="modalWizard"
    >
      <template #body>
        <organisation-wizard
          v-if="appInitComplete && this.showWizard"
          :service-token="token"
          :service-tenant="tenant"
          :allow-cancel="true"
          @onFinalised="completeSetup"
          @onCancel="tryHideWizard"
          @onSizeChanged="changeWizardSize"
        />
      </template>
    </ax-shared-popup>

    <!-- Generic confirmation popup -->
    <ax-shared-popup :size="'md'" :id="'modalConfirm'" ref="modalConfirm">
      <template #body>
        <ax-popup
          :popupContent="(confirmRequest?.modalContent ?? defaultPopupContent)"
          :type="(confirmRequest?.type ?? 'delete')"
          @closePopup="cancelConfirmation"
          @confirmPopup="confirmConfirmation"
        />
      </template>
    </ax-shared-popup>

    <tutorial-wizard v-if="appInitComplete && isLoggedIn && !isAdmin" @toggleNavSettings="toggleNavSettings" @showWizardGuide="showWizardGuide" />

    <!-- Expense Drivers Wizard Guide -->
    <ax-shared-popup :size="'lg'" :id="'expenseDriversWizardGuide'" ref="expenseDriversWizardGuide">
      <template #body>
        <expense-drivers-wizard v-if="appInitComplete" @onClose="expenseDriversWizardGuideClose" />
      </template>
    </ax-shared-popup>

    <!-- Income Drivers Wizard Guide -->
    <ax-shared-popup :size="'lg'" :id="'incomeDriversWizardGuide'" ref="incomeDriversWizardGuide">
      <template #body>
        <income-drivers-wizard v-if="appInitComplete" @onClose="incomeDriversWizardGuideClose" />
      </template>
    </ax-shared-popup>

    <!-- Profit / Loss Wizard Guide -->
    <ax-shared-popup :size="'lg'" :id="'profitLossWizardGuide'" ref="profitLossWizardGuide">
      <template #body>
        <profit-loss-wizard v-if="appInitComplete" @onClose="profitLossWizardGuideClose" />
      </template>
    </ax-shared-popup>

    <!-- Cash Summary Wizard Guide -->
    <ax-shared-popup :size="'lg'" :id="'cashSummaryWizardGuide'" ref="cashSummaryWizardGuide">
      <template #body>
        <cash-summary-wizard v-if="appInitComplete" @onClose="cashSummaryWizardGuideClose" />
      </template>
    </ax-shared-popup>

    <!-- Start Year Wizard Guide -->
    <ax-shared-popup :size="'lg'" :id="'startYearWizardGuide'" ref="startYearWizardGuide">
      <template #body>
        <start-year-wizard v-if="appInitComplete" @onClose="startYearWizardGuideClose" />
      </template>
    </ax-shared-popup>
  </div>
</template>
<style lang="scss">
// Set the variables here.
@import "./app.scss";
</style>
<script>
  import MainLayout from "@/layouts/MainLayout.vue"
  import DialogBackdrop from "@/layouts/DialogBackdrop.vue"
  import store from "@/store"
  import AuthService from "@/services/auth-service"
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/css/index.css";
  import SyncDialog from "@/components/dialogs/SyncDialog";
  import BusinessService from "@/services/business-service";
  import ReviewImportDialog from "./components/dialogs/ReviewImportDialog.vue";
  import MultipleConnections from "./components/dialogs/MultipleConnectionsDialog.vue";
  import OrganisationSelectDialog from "./components/dialogs/OrganisationSelectDialog.vue";
  import CombinedSubscriptionDialog from "./components/dialogs/CombinedSubscriptionDialog.vue";
  import OrganisationWizard from "./components/OrganisationWizard.vue";
  import ExpenseDriversWizardGuideDialog from "./components/dialogs/ExpenseDriversWizardGuideDialog.vue";
  import IncomeDriversWizardGuideDialog from "./components/dialogs/IncomeDriversWizardGuideDialog.vue";
  import ProfitLossWizardGuideDialog from "./components/dialogs/ProfitLossWizardGuideDialog.vue";
  import CashSummaryWizardGuideDialog from "./components/dialogs/CashSummaryWizardGuideDialog.vue";
  import StartYearWizardGuideDialog from "./components/dialogs/StartYearWizardGuideDialog.vue";
  import Popup from "./components/Popup.vue";
  import { useToast } from "vue-toastification";
  import InputError from "@/error/InputError";
  import BusinessStartYearService from "./services/business-start-year-service";
  import eventBus from "@/eventBus";
  const toast = useToast();

  export default({
    name: "app",
    components: {
      MainLayout,
      DialogBackdrop,
      Loading,
      "sync-dialog": SyncDialog,
      "review-import": ReviewImportDialog,
      "multiple-connections": MultipleConnections,
      "ax-popup": Popup,
      "organisation-select": OrganisationSelectDialog,
      "organisation-wizard": OrganisationWizard,
      "subscription-dialog": CombinedSubscriptionDialog,
      "expense-drivers-wizard": ExpenseDriversWizardGuideDialog,
      "income-drivers-wizard": IncomeDriversWizardGuideDialog,
      "profit-loss-wizard": ProfitLossWizardGuideDialog,
      "cash-summary-wizard": CashSummaryWizardGuideDialog,
      "start-year-wizard": StartYearWizardGuideDialog,
    },
    data() {
      return {
        businessStartYearDetail: {
          businessStartYearID: 0,
          businessID: 0,
          startYear: false
        },
        dataReady: false,
        importData: null,
        afterSync: null,
        connectionData: null,
        token: null,
        tenant: null,
        confirmRequest: null,
        defaultPopupContent: {
          title: "<PLACEHOLDER>",
          description: "<PLACEHOLDER>",
          closeBttText: "No",
          confirmBttText: "Yes"
        },
        showWizard: false,
        wizardModalSize: 'md',
        businessIDContext: null,
        subscriptionContext: null,
        screenLocked: false,
        subSize: "md"
      }
    },
    props: {
      msg: String
    },
    beforeCreate() {
      store.dispatch("initialise");
    },
    mounted() {
      eventBus.on('close-all-popups', this.hideAllModals);
    },
    beforeUnmount() {
      eventBus.off('close-all-popups', this.hideAllModals);
    },
    watch: {
      tutorialWizardUpdated() {
        this.loadBusinessStartYearDetail();
      },
      $route (to, from) {
        if (this.$store.getters.isSubscriptionLocked || !this.$store.getters.businessID) {
          this.tryShowLockedScreen(to);
        }
      },
      '$store.state.appInitComplete': {
        handler: function() {
          // delay loading until ready
          if (this.$store.getters.appInitComplete) {
            this.initialAppLoad();
          }
        }
      }
    },
    methods: {
      async initialAppLoad() {
        await this.xeroLogin();

        if (this.$store.getters.isSubscriptionLocked || !this.$store.getters.businessID) {
          this.tryShowLockedScreen(this.$route);
        }
        else {
          await this.loadBusinessStartYearDetail();
        }
        this.getXeroConfig();

        this.dataReady = true;
        store.dispatch("setAppDataReady", true);
      },
      async loadBusinessStartYearDetail() {
        const response = await BusinessStartYearService.checkBusinessStartYear();
        this.businessStartYearDetail = response;
      },
      showWizardGuide(category) {
        if (category == "Expense Drivers") {
          this.$refs.expenseDriversWizardGuide.open();
        } else if (category == "Income Drivers") {
          this.$refs.incomeDriversWizardGuide.open();
        } else if (category == "Profit / Loss") {
          this.$refs.profitLossWizardGuide.open();
        } else if (category == "Cash Summary") {
          this.$refs.cashSummaryWizardGuide.open();
          this.$store.commit("setCashSummaryWizardGuideOpen", true);
        } else if (category == "Start Year") {
          this.$refs.startYearWizardGuide.open();
        }
      },
      toggleNavSettings() {
        this.$refs.layoutRef.toggleNavSettings();
      },
      expenseDriversWizardGuideClose() {
        this.$refs.expenseDriversWizardGuide.close();
      },
      incomeDriversWizardGuideClose() {
        this.$refs.incomeDriversWizardGuide.close();
      },
      profitLossWizardGuideClose() {
        this.$refs.profitLossWizardGuide.close();
      },
      cashSummaryWizardGuideClose() {
        this.$refs.cashSummaryWizardGuide.close();
        this.$store.commit("setCashSummaryWizardGuideOpen", false);
      },
      startYearWizardGuideClose() {
        this.$refs.startYearWizardGuide.close();
      },
      async submitReview() {
        this.hideXeroReviewModal();
        await this.resyncDataFromService(this.afterSync);
      },
      async submitConnection(connection) {
        this.hideMultipleConnectionsModal();

        if (!connection) {
          this.token = null;
          this.tenant = null;
        }

        if (connection?.canReconnect ?? false) {
          try {
            await BusinessService.reconnectXeroAccount({
              token: this.token,
              tenantID: connection.tenantId 
            });

            toast.clear();
            toast.success("Xero tenant reconnected");

            this.reloadPageWithoutParams(); // Force a refresh of the UI, just in case
          }
          catch (ex) {
            toast.clear();
            toast.error("Failed to reconnect Xero tenant, please try again later");
          }
        }
        else if (this.$store.getters.initialSetUpCompleted) {
          this.tenant = connection;
          this.tryShowWizard();
        }
        else {
          store.dispatch("setTempXeroData", { token: this.token, tenant: connection });
          this.$router.push({ path: "signupBusinessInfo" })
        }
      },
      showConfirmation(confirmRequest) {
        this.confirmRequest = confirmRequest;
        this.$refs.modalConfirm.open();
      },
      cancelConfirmation() {
        this.$refs.modalConfirm.close();

        if (this.confirmRequest.cancel) {
          this.confirmRequest.cancel();
          this.confirmRequest.action = null;
          this.confirmRequest.cancel = null;
        }
      },
      confirmConfirmation() {
        this.$refs.modalConfirm.close();

        if (this.confirmRequest.action) {
          this.confirmRequest.action();
          this.confirmRequest.action = null;
          this.confirmRequest.cancel = null;
        }
      },
      logout() {
        this.$router.push("/login")
      },
      async getXeroConfig() {
        try {
          await AuthService.getXeroConfig()
        } catch (error) {
          this.showError(error);
        }
      },
      async xeroLogin() {
        let urlLoc = new URL(location.href);

        const urlCode = urlLoc.searchParams.get("code");
        const isReferral = urlLoc.pathname.toLowerCase() == "/xeroappstoresignin";
        const isUserToken = isReferral || urlLoc.pathname.toLowerCase() == "/xerosignin";

        console.log("urlCode: " + urlCode);
        console.log("isReferral: " + isReferral);
        if (urlCode) {
          await AuthService.xeroLogin(urlCode, isReferral, isUserToken)
          .then((response) => {
            if (response && response.isTenantToken) {
              this.token = response.token;

              const availableConnections = response.connections.filter((c) => c.isAvailable);
              this.connectionData = availableConnections;

              if (!response.connections || response.connections.length == 0) {
                this.showSpecificAlert("Cannot link Xero tenant, no tenants have been provided", 6000, true);
                this.submitConnection(null); // Proceed with no token
              }
              else if (response.connections.length == 1 && (availableConnections?.length ?? 0) == 0) {
                this.showSpecificAlert("Cannot link Xero tenant, an organisation linked to this tenant already exists", 6000, true);
                this.submitConnection(null); // Proceed with no token
              }
              else if (response.connections.length > 1 && (availableConnections?.length ?? 0) == 0) {
                this.showSpecificAlert("Cannot link Xero tenant(s), organisations linked to these tenants already exist", 6000, true);
                this.submitConnection(null); // Proceed with no token
              }
              else if (this.connectionData.length > 1) {
                this.showMultipleConnectionsModal(); // More than on option available, let the user decide
              }
              else {
                this.submitConnection(this.connectionData[0]); // Only one token available, so use this
              }
            }
            else {
              if (store.getters.initialSetUpCompleted) {
                
                if (!response?.resyncStatus) {
                  this.showSuccess("Login successful.");
                }
                else if (response?.resyncStatus && response.resyncStatus.success) {
                  toast.success("Login successful - Data synced.", { queue: true });
                }
                else if (response?.resyncStatus && !response.resyncStatus.success) {
                  // Service failed to sync - not good, but won't prevent logging in
                  this.showSpecificAlert(response.resyncStatus.errorMessage, 8000, true);
                }
                else {
                  toast.error("Something went wrong...");
                }
                
                this.$router.push("/");
              }
              else {
                store.dispatch("updateXeroConnected", true);
                this.$router.push({ path: "signupBusinessInfo" })
              }
            }
          }).catch((err) => {           
            this.$router.push("/");
            this.showSpecificAlert(err.response.data);
          });
        }
      },
      reloadPageWithoutParams() {
        setTimeout(() => {
          location = location.href.split("?")[0];
        }, 500);
      },
      showError(error) {
        if (!queue)
          toast.clear();

        toast.error(error.message);
      },
      showModal(ref) {
        this.$refs[ref].open();
      },
      hideModal(ref) {
        this.$refs[ref].close();
      },
      hideAllModals() {
        this.$refs.modalSync.close();
        this.$refs.importReview.close();
        this.$refs.multipleConnectionsModal.close();
        this.$refs.modalOrganisationSelect.close();
        this.$refs.modalSubscription.close();
        this.$refs.modalWizard.close();
        this.$refs.modalConfirm.close();
        this.$refs.expenseDriversWizardGuide.close();
        this.$refs.incomeDriversWizardGuide.close();
        this.$refs.profitLossWizardGuide.close();
        this.$refs.cashSummaryWizardGuide.close();
        this.$refs.startYearWizardGuide.close();
      },
      showSuccess(msg) {
        toast.success(msg);
      },
      async cancelImport() {
        this.hideXeroReviewModal();
        this.showSpecificAlert("Resync cancelled.");
        this.afterSync = null;
        this.importData = null;
      },
      async resyncDataFromService(afterSync, prioritySystemSync) {
        var success = false;
        this.showResyncModal();
        await BusinessService.resyncFromService(false, this.importData, prioritySystemSync)
          .then(async (response) => {
            if (response && response.success) {
              if (response.showReview) {
                var importData = BusinessService.mapReviewDataToViewModels(response.accountsList);
                this.showXeroReviewModal(importData, afterSync);
                this.hideResyncModal();

                return success;
              } else {
                if (afterSync) {
                  await afterSync();
                }
                toast.clear();
                toast.success("Resync completed successfully.");
              }

              this.importData = null;
              this.afterSync = null;
              success = true;
            } else if (response && !response.success) {
              if (response.errorType == "refresh_failure") {
                const scopes = "openid offline_access accounting.settings accounting.reports.read";
                AuthService.RedirectToXero(scopes);
              }

              this.importData = null;
              this.afterSync = null;
              this.showSpecificAlert(response.errorMessage);
            }
          }).catch((err) => {
            this.showSpecificAlert(err.response.data);

            this.importData = null;
            this.afterSync = null;
            success = false;
          });
          
          this.hideResyncModal();
          return success;
      },
      showResyncModal() {
        store.commit("setIsSyncing", true);
        this.$refs.modalSync.open();
      },
      hideResyncModal() {
        setTimeout(() => {
          store.commit("setIsSyncing", false);
          this.$refs.modalSync.close();
        }, 500);
      },
      showXeroReviewModal(importData, afterSync) {
        this.importData = importData;
        this.afterSync = afterSync;
        this.$refs.importReview.open();
      },
      hideXeroReviewModal() {
        this.$refs.importReview.close();
      },
      showMultipleConnectionsModal() {
        this.$refs.multipleConnectionsModal.open();
      },
      hideMultipleConnectionsModal() {
        this.$refs.multipleConnectionsModal.close();
      },
      showInputErrorAlert(error, defaultMessage) {
        toast.clear();
        if (error instanceof InputError) {
          toast.error(error.message);
        } else {
          toast.error(defaultMessage);
        }
      },
      showAlert() {
        this.showGenericAlert();
      },
      showSpecificAlert(error, duration, queue) {
        toast.clear();
        toast.error(error, { duration: duration ?? 3000, queue: queue ?? false });
      },
      showGenericAlert() {
        toast.clear();
        toast.error("Something went wrong! Please try again.");
      },
      tryShowWizard() {
        this.$refs.modalWizard.open();
        this.showWizard = true;
        this.screenLocked = false;
      },
      tryHideWizard() {
        this.token = null;
        this.tenant = null;

        this.$refs.modalWizard.close();
        this.showWizard = false;

        this.showOrgSelectScreen();
      },
      changeWizardSize(size) {
        this.wizardModalSize = size;
        console.log("Wizard size changed to: " + size);
      },
      async completeSetup(businessID) {
        await AuthService.switchOrganisation(businessID);
        
        toast.clear();
        toast.success("Successful");

        this.reloadPageWithoutParams();
      },
      showSubscriptionModal(businessID, fromOrgScreen) {
        this.businessIDContext = businessID ?? store.getters.businessID;
        this.$refs.modalSubscription.open();
        this.showWizard = false;
        this.screenLocked = false;

        if (!this.subscriptionContext) {
          this.subscriptionContext = fromOrgScreen ? 'org' : 'menuScreen';
        }
      },
      hideSubscriptionModal() {
        this.businessIDContext = null;
        this.$refs.modalSubscription.close();

        if (this.subscriptionContext == 'org') {
          this.showOrgSelectScreen();
        }

        this.subscriptionContext = null;
      },
      tryShowLockedScreen(to) {
        if (to.meta.unlocked) {
          return;
        }

        const urlCode = new URL(location.href).searchParams.get("code");
        const hasAuthenticatedSession = sessionStorage.getItem("sessionAuthenticated") === "true" && store.getters.jwtToken;
        const isUnlocked = to.meta.unlocked;

        if (!store.getters.isAdmin && !urlCode && !isUnlocked && hasAuthenticatedSession) {
          this.showOrgSelectScreen();
        }
      },
      showOrgSelectScreen() {
        if (!this.screenLocked) {
          this.showModal('modalOrganisationSelect');
          this.screenLocked = true;
        }
      },
      hideOrgSelectModal() {
        this.hideModal('modalOrganisationSelect');
        this.screenLocked = false;
        const subscriptionLocked = store.getters.isSubscriptionLocked;
        if (subscriptionLocked) {
          this.$router.go(0);
        }
      }
    },
    computed: {
      showWizard() {
        return store.getters.isLoggedIn && !store.getters.isAdmin && store.getters.initialSetUpCompleted &&
          this.businessStartYearDetail && !this.businessStartYearDetail.tutorialWizardCompleted &&
          this.$router.currentRoute.value.name != "FinaliseAccount";
      },
      tutorialWizardUpdated() {
        return this.$store.getters.tutorialWizardUpdate;
      },
      layout() {
        var metaData = this.$route.meta;
        return metaData?.layout ? metaData?.layout : "";
      },
      key() {
        return this.$route.path;
      },
      allowOrgSelectClose() {
        return store.getters.businessID && !store.getters.isSubscriptionLocked;
      },
      isAuthenticated() {
        return store.getters.jwtToken;
      },
      appInitComplete() {
        return store.getters.appInitComplete;
      },
      isAffiliate() {
        return store.getters.isAffiliate;
      },
      isAdmin() {
        return store.getters.isAdmin;
      },
      isLoggedIn() {
        return store.getters.isLoggedIn;
      }
    }
  })
</script>
